import { FileText, Percent, Scale, AlertTriangle, Check, CalendarClock } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  CFOPS,
  CSOSNS,
  CSTS_IBS_CBS,
  CSTS_ICMS,
  CSTS_PIS_COFINS,
  ORIGENS,
  UNIDADES,
  camposFaltando,
} from "./fiscalOptions";

/**
 * CARDÁPIO → PRODUTOS → aba NOTA FISCAL.
 *
 * Cadastro fiscal do produto — o que a SEFAZ exige por item pra autorizar a
 * NFC-e (modelo 65). Sem NCM e CFOP a nota é rejeitada, então esses dois são
 * tratados como obrigatórios e travam o indicador de status no topo.
 *
 * Os valores ficam em products.fiscal_config (JSON), mesmo padrão de
 * print_config. Quem consome é o módulo de emissão.
 *
 * IMPORTANTE: a classificação fiscal (NCM/CFOP/CST) é responsabilidade da
 * contabilidade da loja. A UI sugere os códigos mais comuns em food service,
 * mas não decide por ela — NCM errado não gera rejeição na hora, gera autuação
 * depois.
 */

function Bloco({ icon: Icon, cor, titulo, descricao, children }) {
  return (
    <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b bg-muted/30 flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl ${cor} flex items-center justify-center shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <Label className="text-sm font-semibold block">{titulo}</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5">{descricao}</p>
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Campo({ label, hint, obrigatorio, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium flex items-center gap-1.5">
        {label}
        {obrigatorio && <span className="text-red-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-[10.5px] text-muted-foreground leading-snug">{hint}</p>}
    </div>
  );
}

function CampoSelect({ valor, onChange, opcoes, placeholder }) {
  return (
    <Select value={valor || ""} onValueChange={onChange}>
      <SelectTrigger className="h-9 text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {opcoes.map((o) => (
          <SelectItem key={o.v} value={o.v} className="text-sm">
            {o.l}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function ProdutoNotaFiscalTab({ value, onChange, regimeTributario = "simples" }) {
  const fc = value || {};
  const set = (campo, v) => onChange({ ...fc, [campo]: v });

  const soDigitos = (v, max) => (v || "").replace(/\D/g, "").slice(0, max);

  const isSimples = regimeTributario === "simples";
  const faltando = camposFaltando(fc, regimeTributario);
  const pronto = faltando.length === 0;

  return (
    <div className="space-y-5">
      {/* Status — o lojista precisa saber num relance se o item emite ou não */}
      <div
        className={`flex items-start gap-3 p-4 rounded-2xl border-2 ${
          pronto
            ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800"
            : "bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800"
        }`}
      >
        {pronto ? (
          <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        )}
        <div className="text-sm">
          {pronto ? (
            <>
              <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                Produto pronto para emitir nota.
              </span>
              <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80 mt-0.5">
                Os campos obrigatórios da NFC-e estão preenchidos.
              </p>
            </>
          ) : (
            <>
              <span className="font-semibold text-amber-700 dark:text-amber-300">
                Faltam campos obrigatórios: {faltando.join(", ")}.
              </span>
              <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 mt-0.5">
                Sem eles a SEFAZ rejeita a nota deste item e a venda fica sem cupom fiscal.
              </p>
            </>
          )}
        </div>
      </div>

      <Bloco
        icon={Scale}
        cor="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
        titulo="Classificação da mercadoria"
        descricao="Confirme estes códigos com a contabilidade da loja. Classificação errada não trava a venda hoje — gera autuação depois."
      >
        <Campo label="NCM" obrigatorio hint="8 dígitos. Identifica o tipo de mercadoria.">
          <Input
            value={fc.ncm || ""}
            onChange={(e) => set("ncm", soDigitos(e.target.value, 8))}
            placeholder="00000000"
            inputMode="numeric"
            className="h-9 text-sm font-mono"
          />
        </Campo>

        <Campo label="CEST" hint="7 dígitos. Só para itens em substituição tributária.">
          <Input
            value={fc.cest || ""}
            onChange={(e) => set("cest", soDigitos(e.target.value, 7))}
            placeholder="0000000"
            inputMode="numeric"
            className="h-9 text-sm font-mono"
          />
        </Campo>

        <Campo label="CFOP padrão" obrigatorio hint="Natureza da operação na venda ao consumidor.">
          <CampoSelect
            valor={fc.cfop}
            onChange={(v) => set("cfop", v)}
            opcoes={CFOPS}
            placeholder="Selecione o CFOP"
          />
        </Campo>

        <Campo label="Origem da mercadoria" obrigatorio hint="Food service é quase sempre 0 — Nacional.">
          <CampoSelect
            valor={fc.origem}
            onChange={(v) => set("origem", v)}
            opcoes={ORIGENS}
            placeholder="Selecione a origem"
          />
        </Campo>

        <Campo label="Unidade tributável" hint="Unidade que vai na nota. Pode diferir da unidade de venda.">
          <CampoSelect
            valor={fc.unidade_tributavel}
            onChange={(v) => set("unidade_tributavel", v)}
            opcoes={UNIDADES.map((u) => ({ v: u, l: u }))}
            placeholder="UN"
          />
        </Campo>
      </Bloco>

      <Bloco
        icon={Percent}
        cor="bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400"
        titulo={isSimples ? "ICMS — Simples Nacional (CSOSN)" : "ICMS — Regime normal (CST)"}
        descricao={
          isSimples
            ? "A loja está no Simples, então a situação tributária usa CSOSN."
            : "A loja está no regime normal, então a situação tributária usa CST."
        }
      >
        {isSimples ? (
          <Campo label="CSOSN" obrigatorio hint="O mais comum em food service é 102.">
            <CampoSelect
              valor={fc.csosn}
              onChange={(v) => set("csosn", v)}
              opcoes={CSOSNS}
              placeholder="Selecione o CSOSN"
            />
          </Campo>
        ) : (
          <Campo label="CST do ICMS" obrigatorio>
            <CampoSelect
              valor={fc.cst_icms}
              onChange={(v) => set("cst_icms", v)}
              opcoes={CSTS_ICMS}
              placeholder="Selecione o CST"
            />
          </Campo>
        )}

        <Campo label="Alíquota de ICMS (%)" hint="Deixe vazio quando não houver ICMS destacado.">
          <Input
            value={fc.aliquota_icms ?? ""}
            onChange={(e) => set("aliquota_icms", e.target.value.replace(/[^\d.,]/g, ""))}
            placeholder="0,00"
            inputMode="decimal"
            className="h-9 text-sm font-mono"
          />
        </Campo>
      </Bloco>

      <Bloco
        icon={FileText}
        cor="bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400"
        titulo="PIS e COFINS"
        descricao="No Simples Nacional costuma ser CST 49 com alíquota zero, mas quem confirma é a contabilidade."
      >
        <Campo label="CST de PIS/COFINS">
          <CampoSelect
            valor={fc.cst_pis_cofins}
            onChange={(v) => set("cst_pis_cofins", v)}
            opcoes={CSTS_PIS_COFINS}
            placeholder="Selecione o CST"
          />
        </Campo>

        <div className="grid grid-cols-2 gap-3">
          <Campo label="Alíquota PIS (%)">
            <Input
              value={fc.aliquota_pis ?? ""}
              onChange={(e) => set("aliquota_pis", e.target.value.replace(/[^\d.,]/g, ""))}
              placeholder="0,00"
              inputMode="decimal"
              className="h-9 text-sm font-mono"
            />
          </Campo>
          <Campo label="Alíquota COFINS (%)">
            <Input
              value={fc.aliquota_cofins ?? ""}
              onChange={(e) => set("aliquota_cofins", e.target.value.replace(/[^\d.,]/g, ""))}
              placeholder="0,00"
              inputMode="decimal"
              className="h-9 text-sm font-mono"
            />
          </Campo>
        </div>
      </Bloco>

      <Bloco
        icon={CalendarClock}
        cor="bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400"
        titulo="Reforma tributária — IBS e CBS"
        descricao="Obrigatório para lojas do Simples Nacional a partir de 04/01/2027. Pode ficar vazio até lá."
      >
        <Campo label="CST do IBS/CBS" hint="Situação tributária dos novos impostos (NT 2025.002).">
          <CampoSelect
            valor={fc.cst_ibs_cbs}
            onChange={(v) => set("cst_ibs_cbs", v)}
            opcoes={CSTS_IBS_CBS}
            placeholder="Selecione o CST"
          />
        </Campo>

        <Campo label="Classificação tributária" hint="cClassTrib — código de 6 dígitos que detalha o CST.">
          <Input
            value={fc.classificacao_tributaria || ""}
            onChange={(e) => set("classificacao_tributaria", soDigitos(e.target.value, 6))}
            placeholder="000000"
            inputMode="numeric"
            className="h-9 text-sm font-mono"
          />
        </Campo>
      </Bloco>
    </div>
  );
}
